/**
 * Two panels, one window — the M2 demo: a real tutoring session with the editor
 * and the tutor side by side and no pasting.
 *
 * M3 adds the third panel (the skill map), nickname+PIN login and session resume.
 * There is no login here on purpose: the API is protected by a shared facilitator
 * token, so this runs on a trusted machine and the learner is chosen up front.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { api, ApiError, bufferRun } from "./api";
import type {
  Domain,
  EditorRunPayload,
  EndSessionResult,
  OpenSession,
  SessionInfo,
  SkillMap as SkillMapData,
} from "./api";
import { EditorPanel } from "./components/EditorPanel";
import { SkillMap } from "./components/SkillMap";
import { SessionOutcome, TutorPanel } from "./components/TutorPanel";
import type { ChatMessage } from "./components/TutorPanel";
import { Aurora } from "./Aurora";
import { createRunner } from "./runners";
import { insertAt } from "./sounds";
import type { SoundsCatalogue } from "./sounds";
import type { Me } from "./api";
import { WhoAmI } from "./auth";
import { whenText } from "./when";
import type { Runner, RunResult } from "./runners/types";

/**
 * `choosing` is Home: signed in, nothing open, pick a playground.
 * `finished` is the screen a saved session lands on — the map with what moved on it,
 * and one way out. It is deliberately not `choosing`: a learner who has just finished
 * should see what they did before the picker asks them what is next.
 */
type Phase = "choosing" | "starting" | "live" | "finished" | "failed";

export default function App({ me, onSignOut }: { me: Me; onSignOut: () => void }) {
  const [phase, setPhase] = useState<Phase>("choosing");
  const [domains, setDomains] = useState<Domain[]>([]);
  // A signed-in learner never types their own code: they *are* the learner, and the
  // server would refuse anyone else's anyway. A facilitator can type one, because
  // sitting beside a learner and starting a session for them is the job.
  const [learnerCode, setLearnerCode] = useState(me.learner_code ?? "");
  const [domainSlug, setDomainSlug] = useState("python");
  const [problem, setProblem] = useState<string | null>(null);

  const [session, setSession] = useState<SessionInfo | null>(null);
  const [runner, setRunner] = useState<Runner | null>(null);

  const [code, setCode] = useState("");
  const [stdinText, setStdinText] = useState("");
  // The question a paused program is waiting on, and what the learner is typing back.
  const [asking, setAsking] = useState<string | null>(null);
  const [answerText, setAnswerText] = useState("");
  const [running, setRunning] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<RunResult | null>(null);
  // Whether a runner is currently making sound. Strudel's Stop used to be permanently
  // enabled because `canStop` is a static property of the runner, so it said nothing
  // about whether there was anything to stop — and a learner reasonably read a live
  // Stop button as "it is playing", which was the only feedback they had.
  const [playing, setPlaying] = useState(false);

  /** Runs since the learner last spoke. This is the buffer the whole design turns on. */
  const [buffered, setBuffered] = useState<EditorRunPayload[]>([]);

  // Has the learner actually said anything to the tutor? A session they never spoke in
  // is not a session — they came to look at their skill map, or opened the wrong
  // playground — and it gets thrown away rather than written down. See `leave`.
  const [spoke, setSpoke] = useState(false);

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [streaming, setStreaming] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [ending, setEnding] = useState(false);
  const [outcome, setOutcome] = useState<EndSessionResult | null>(null);

  const [sounds, setSounds] = useState<SoundsCatalogue | null>(null);
  const [soundsLoading, setSoundsLoading] = useState(false);
  const [soundsError, setSoundsError] = useState<string | null>(null);
  const [cursor, setCursor] = useState(0);

  // The skill map. Loaded on demand rather than with the session: it is a few
  // kilobytes and most turns never open it.
  const [map, setMap] = useState<SkillMapData | null>(null);
  const [mapOpen, setMapOpen] = useState(false);
  const [mapProblem, setMapProblem] = useState<string | null>(null);
  // Concepts that moved in the session just ended, so the map can single them out.
  const [litUp, setLitUp] = useState<string[]>([]);

  // Sessions this learner could pick up again.
  const [resumable, setResumable] = useState<OpenSession[]>([]);
  // Distinguishes "a facilitator needs to record consent" from "something broke".
  const [consentBlocked, setConsentBlocked] = useState(false);

  const startedAt = useRef<number>(Date.now());

  useEffect(() => {
    api
      .domains()
      .then(setDomains)
      .catch((error: unknown) =>
        setProblem(
          error instanceof ApiError && error.status === 401
            ? "Your sign-in has expired. Reload the page and sign in again."
            : `Cannot reach the backend. Is it running? (${String(error)})`,
        ),
      );
  }, []);

  const begin = useCallback(async () => {
    setPhase("starting");
    setProblem(null);
    setConsentBlocked(false);
    setSpoke(false);
    setOutcome(null);
    try {
      const info = await api.startSession(learnerCode.trim(), domainSlug);
      setSession(info);
      const created = createRunner(info.runner);
      setRunner(created);
      // Where they left off, if they have been here before in this domain. An empty
      // string is a real answer — they cleared the editor on purpose — so only a
      // genuine absence falls back to the starter code.
      setCode(info.restored_code ?? created.starterCode);
      startedAt.current = Date.now();
      setMessages([
        {
          role: "system",
          text:
            `Session ${info.session_number} · ${info.domain_title}` +
            (info.project ? ` · ${info.project.title}` : ""),
        },
      ]);
      setPhase("live");

      // Let the tutor speak first. A blank screen asks a ten-year-old to start a
      // conversation with a stranger, which is the least welcoming moment we could
      // have picked for the first thing they see. Streamed like any other reply, so
      // it arrives a few words at a time rather than appearing all at once.
      setStreaming("");
      api
        .streamOpening(info.session_id, {
          onDelta: (text) => setStreaming((previous) => (previous ?? "") + text),
        })
        .then(() => {
          setStreaming((text) => {
            if (text) setMessages((previous) => [...previous, { role: "tutor", text }]);
            return null;
          });
        })
        .catch(() => {
          // Not worth interrupting them for: they can simply type first, which is
          // what happened before this existed.
          setStreaming(null);
        });

      // Only ask when the session says there is one. Firing a request we know
      // will 404 puts a red line in the console on every Python session, which
      // trains everyone to ignore console errors.
      setSounds(null);
      setSoundsError(null);
      if (info.has_sounds) {
        setSoundsLoading(true);
        api
          .sounds(info.domain_slug)
          .then(setSounds)
          .catch((soundsProblem: unknown) =>
            setSoundsError(
              `Could not load the sound list: ${
                soundsProblem instanceof Error ? soundsProblem.message : String(soundsProblem)
              }`,
            ),
          )
          .finally(() => setSoundsLoading(false));
      }
    } catch (error) {
      // A 403 here is not a fault: it means no caregiver consent has been recorded
      // for this learner, or it was withdrawn. The API's message names the command a
      // facilitator needs to run, so show it as-is rather than wrapping it.
      setConsentBlocked(error instanceof ApiError && error.status === 403);
      setProblem(error instanceof Error ? error.message : String(error));
      setPhase("failed");
    }
  }, [learnerCode, domainSlug]);

  // Look for sessions to pick up again while they type the code. Debounced: without
  // it this fires on every keystroke, and "L-0007" is eight requests.
  //
  // Across every playground, not just the selected one. An open session is a
  // reconnection — the reload, the dropped connection, the Home button pressed by
  // mistake — and filtering by the radio that happens to be ticked meant a learner who
  // walked out of a Strudel session was shown nothing at all, because Home comes up on
  // Python. The one affordance that exists to undo that mistake was hidden behind
  // guessing which playground the mistake happened in. The rows name their own
  // playground now, and `resume` takes the domain from the session rather than the form.
  useEffect(() => {
    if (phase === "live") return;
    const code = learnerCode.trim();
    if (code.length < 3) {
      setResumable([]);
      return;
    }
    const timer = setTimeout(() => {
      api
        .learnerSessions(code)
        .then((rows) => setResumable(rows.filter((r) => r.status === "open")))
        // A learner code that does not exist yet is a 404, and that is not an
        // error worth showing while someone is still typing.
        .catch(() => setResumable([]));
    }, 400);
    return () => clearTimeout(timer);
  }, [learnerCode, phase]);

  const openMap = useCallback(
    async (learner: string, domain: string, lit: string[] = []) => {
      setMapProblem(null);
      setLitUp(lit);
      setMapOpen(true);
      try {
        setMap(await api.skillMap(learner, domain));
      } catch (error) {
        setMapProblem(error instanceof Error ? error.message : String(error));
      }
    },
    [],
  );

  /**
   * Reopen a session where it was left.
   *
   * One request: the transcript carries the session payload too, so the runner and
   * the project come from the session being resumed rather than being guessed.
   */
  const resume = useCallback(async (sessionId: string) => {
    setPhase("starting");
    setProblem(null);
    try {
      const reopened = await api.transcript(sessionId);
      const info = reopened.session;
      if (!info) throw new Error("that session could not be reopened");

      setSession(info);
      const created = createRunner(info.runner);
      setRunner(created);

      // The saved editor first: it is the last thing they *typed*. The last thing they
      // ran is the fallback, and the starter code the fallback of last resort.
      const lastRun = reopened.turns
        .flatMap((turn) => turn.runs)
        .filter((run) => run.code)
        .at(-1);
      setCode(info.restored_code ?? lastRun?.code ?? created.starterCode);

      setMessages([
        {
          role: "system",
          text:
            `Picked up session ${info.session_number} · ${info.domain_title}` +
            (info.project ? ` · ${info.project.title}` : ""),
        },
        ...reopened.turns.map((turn) => ({
          role: turn.role === "assistant" ? ("tutor" as const) : ("learner" as const),
          text: turn.text,
        })),
      ]);
      startedAt.current = Date.now();
      // Picked up rather than started, so whether it counts as a session was already
      // decided: if there is a learner turn in the transcript, it is real. One that was
      // only ever greeted is still a look-around, and leaving again still discards it.
      setSpoke(reopened.turns.some((turn) => turn.role !== "assistant"));
      setOutcome(null);
      setPhase("live");

      setSounds(null);
      setSoundsError(null);
      if (info.has_sounds) {
        setSoundsLoading(true);
        api
          .sounds(info.domain_slug)
          .then(setSounds)
          .catch(() => setSoundsError("Could not load the sound list."))
          .finally(() => setSoundsLoading(false));
      }
    } catch (error) {
      setProblem(error instanceof Error ? error.message : String(error));
      setPhase("failed");
    }
  }, []);

  useEffect(() => {
    if (!runner) return;
    if (runner.onAsk !== undefined) {
      runner.onAsk = (prompt) => {
        setAsking(prompt);
        setAnswerText("");
      };
    }
    return () => {
      if (runner.onAsk !== undefined) runner.onAsk = null;
    };
  }, [runner]);

  // A drawing runner can fail long after the run was reported: a sketch that has been
  // animating happily for three seconds hits an undefined array element and the canvas
  // freezes. Nothing about the run that produced it can carry that, so it arrives on
  // its own — shown in the panel, and buffered so the tutor sees the crash rather than
  // only hearing "it stopped".
  useEffect(() => {
    if (!runner || runner.onLateError === undefined) return;
    runner.onLateError = (message) => {
      const result: RunResult = { outcome: "error", detail: message };
      setLastResult(result);
      setPlaying(false);
      setBuffered((previous) =>
        bufferRun(previous, {
          code: latestCode.current,
          outcome: result.outcome,
          detail: result.detail,
          language: runner.language,
        }),
      );
    };
    return () => {
      if (runner.onLateError !== undefined) runner.onLateError = null;
    };
  }, [runner]);

  const sendAnswer = useCallback(() => {
    if (!runner?.answerInput) return;
    // Cleared before handing it over: the program resumes immediately and the box must
    // not still hold the last answer when it asks the next question.
    const text = answerText;
    setAsking(null);
    setAnswerText("");
    runner.answerInput(text);
  }, [runner, answerText]);

  // Save the editor a few seconds after they stop typing.
  //
  // "Saved when you end the session" is a promise a closed laptop breaks, and losing
  // half-built work is the thing this feature exists to prevent. Debounced rather than
  // per-keystroke: the request is tiny and does no model work, but a save per character
  // would still be hundreds of requests for one sitting.
  //
  // Deliberately silent. A failed save must not interrupt someone mid-thought, and the
  // next one is a few seconds away.
  //
  // The debounce alone is not enough: a tab closing inside the window cancels the
  // pending timer and loses precisely the work this exists to keep. Found by a browser
  // test that navigated away mid-debounce and got a previous session's code back. So a
  // `pagehide` handler saves immediately with `keepalive`, which lets the request
  // outlive the document.
  const latestCode = useRef(code);
  latestCode.current = code;

  // A session on its way to being thrown away must not be written to on the way out.
  // Going Home unmounts the playground, the autosave flushes on unmount, and the flush
  // then lands on a session the discard has already deleted — a 404 in the console of
  // an app whose console is otherwise clean, and a `session_id` pointing at nothing if
  // it wins the race instead. Anything typed is already saved: the debounce is two and
  // a half seconds, and the discard clears the pointer rather than the code.
  const discarded = useRef<string | null>(null);

  useEffect(() => {
    if (phase !== "live" || !session) return;
    const sessionId = session.session_id;

    const timer = setTimeout(() => {
      void api.saveEditor(sessionId, latestCode.current).catch(() => {});
    }, 2_500);

    const flush = () => {
      if (discarded.current === sessionId) return;
      api.saveEditorOnExit(sessionId, latestCode.current);
    };
    // `pagehide` fires where `beforeunload` is unreliable (notably mobile Safari);
    // `visibilitychange` catches a backgrounded tab that never formally unloads.
    const onHidden = () => {
      if (document.visibilityState === "hidden") flush();
    };
    window.addEventListener("pagehide", flush);
    document.addEventListener("visibilitychange", onHidden);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("pagehide", flush);
      document.removeEventListener("visibilitychange", onHidden);
      // Unmounting is also a departure — a domain switch, or the app being torn down.
      flush();
    };
  }, [code, session, phase]);

  // Closing the tab is the most ordinary way a look-around ends, and a plain `fetch`
  // dies with the document — so the discard goes out with `keepalive`, the same trick
  // the editor save uses one effect above.
  //
  // `pagehide` only, deliberately: the editor save also listens on `visibilitychange`
  // because saving a backgrounded tab is free, but *discarding* one would delete the
  // session of a learner who switched tabs to look something up.
  useEffect(() => {
    if (phase !== "live" || !session || spoke) return;
    const sessionId = session.session_id;
    const drop = () => {
      discarded.current = sessionId;
      api.abandonOnExit(sessionId);
    };
    window.addEventListener("pagehide", drop);
    return () => window.removeEventListener("pagehide", drop);
  }, [phase, session, spoke]);

  useEffect(() => () => runner?.dispose(), [runner]);

  // The pre-supplied answers box exists only where the program *cannot* stop and ask.
  // Showing both would be two mechanisms for one thing, and the learner would have to
  // work out which of them their program will use.
  const needsStdin = runner?.language === "python" && !runner?.asksInteractively;

  const handleRun = useCallback(async () => {
    if (!runner) return;
    setRunning(true);
    setLoadingMessage(null);
    try {
      await runner.load((message) => setLoadingMessage(message));
      setLoadingMessage(null);

      // Captured now and the box emptied, so the next Run cannot silently reuse the
      // last one's answers. A learner who runs twice and gets a different result from
      // identical-looking state has no way to work out why.
      const answers = stdinText.split("\n").filter((line) => line.trim().length > 0);
      setStdinText("");
      setAsking(null);

      // No answers means no answers — do not hand the runner an empty provider. That
      // reads as "answers were supplied" and every input() comes back blank without
      // complaint; with no provider at all the learner gets a straight explanation
      // that the program asked a question and nothing was there to answer it.
      let index = 0;
      const result = await runner.run(
        code,
        answers.length > 0
          ? () => (index < answers.length ? (answers[index++] as string) : null)
          : undefined,
      );

      setAsking(null);
      setLastResult(result);
      setPlaying(result.outcome === "played" || result.outcome === "drawing");
      // Buffer it. No API call: it goes with the learner's next message. Only the
      // newest runs are kept: the server refuses a longer list, and the tutor renders
      // all but the last few as one elided line anyway, so the oldest are the ones
      // worth losing.
      setBuffered((previous) =>
        bufferRun(previous, {
          code,
          outcome: result.outcome,
          detail: result.detail,
          language: runner.language,
        }),
      );
    } finally {
      setRunning(false);
    }
  }, [runner, code, stdinText]);

  // The element a drawing runner draws into. A callback ref rather than an object one
  // because the runner has to be told when it appears *and* when it goes away, and
  // because a new runner (a domain switch) has to be handed the element again.
  const attachSurface = useCallback(
    (element: HTMLDivElement | null) => runner?.attachSurface?.(element),
    [runner],
  );

  const pickSound = useCallback(
    (name: string) => {
      setCode((current) => {
        const { text, cursor: next } = insertAt(current, cursor, name);
        setCursor(next);
        return text;
      });
    },
    [cursor],
  );

  const handleStop = useCallback(() => {
    void runner?.stop();
    setPlaying(false);
    setAsking(null);
  }, [runner]);

  const send = useCallback(async () => {
    if (!session) return;
    // The moment a look-around becomes a session. Set before the request rather than
    // after it: a turn that fails halfway still put the learner's words in the
    // transcript, and the server would refuse to discard it anyway.
    setSpoke(true);
    const text = draft.trim();
    const runs = buffered;
    setDraft("");
    setBuffered([]);
    setMessages((previous) => [
      ...previous,
      { role: "learner", text: text || "(look at this)", runCount: runs.length },
    ]);
    setBusy(true);
    setStreaming("");

    let assembled = "";
    // A session is one sitting, and the server ends one that has gone quiet for long
    // enough (D47). The realistic way to meet a 410 is coming back from lunch to a tab
    // that is still open.
    let finished = false;
    try {
      await api.streamTurn(
        session.session_id,
        { text, runs },
        {
          onDelta: (chunk) => {
            assembled += chunk;
            setStreaming(assembled);
          },
          onRefusal: (message) => {
            assembled = message;
          },
          onError: (message) => {
            assembled = `Something went wrong: ${message}`;
          },
        },
      );
    } catch (error) {
      finished = error instanceof ApiError && error.status === 410;
      if (!finished) {
        assembled = `Could not reach the tutor: ${error instanceof Error ? error.message : String(error)}`;
      }
    } finally {
      setStreaming(null);
      // Nothing to show from a session that had already ended — the words go back in
      // the box below instead, so nothing they typed is lost.
      if (!finished) setMessages((previous) => [...previous, { role: "tutor", text: assembled }]);
      setBusy(false);
    }

    if (finished) {
      setDraft(text);
      setBuffered(runs);
      await begin();
      setMessages((previous) => [
        ...previous,
        {
          role: "system",
          text: "That session finished while you were away, so this is a new one — your message is still in the box.",
        },
      ]);
    }
  }, [session, draft, buffered, begin]);

  /**
   * Save the session. The request and nothing else, so the two callers can differ in
   * what they do afterwards: **End session** shows the learner what moved, **Sign out**
   * has nowhere to show it.
   */
  const save = useCallback(async () => {
    if (!session) return null;
    const minutes = Math.max(1, Math.round((Date.now() - startedAt.current) / 60_000));
    return api.endSession(session.session_id, minutes, code);
  }, [session, code]);

  const end = useCallback(async () => {
    if (!session || ending || outcome) return;
    setEnding(true);
    try {
      const result = await save();
      if (!result) return;
      setOutcome(result);
      setPhase("finished");
      // The map is most motivating in the moment the graph has just changed, so it is
      // what the finished screen is built around, with this session's concepts lit.
      void openMap(
        session.learner_code,
        session.domain_slug,
        result.deltas.map((delta) => delta.concept_id),
      );
    } catch (error) {
      setMessages((previous) => [
        ...previous,
        {
          role: "system",
          text: `Could not save the session: ${error instanceof Error ? error.message : String(error)}`,
        },
      ]);
    } finally {
      setEnding(false);
    }
  }, [session, ending, outcome, save, openMap]);

  /**
   * Back to Home, with everything about the playground put down.
   *
   * Every field is reset explicitly rather than by remounting, because a stale one is
   * the kind of bug that shows a learner somebody else's code: `restored_code` arrives
   * per session, and `code` left over from the last one would be written straight back
   * into the editor autosave.
   */
  const toHome = useCallback(() => {
    setPhase("choosing");
    setSession(null);
    // Clearing this fires the dispose effect below, which stops any sound still playing.
    setRunner(null);
    setSpoke(false);
    setOutcome(null);
    setMessages([]);
    setStreaming(null);
    setDraft("");
    setBuffered([]);
    setCode("");
    setLastResult(null);
    setPlaying(false);
    setMapOpen(false);
    setMap(null);
    setLitUp([]);
    setProblem(null);
    setConsentBlocked(false);
    setSounds(null);
    setSoundsError(null);
    setAsking(null);
    setAnswerText("");
    setStdinText("");
  }, []);

  /**
   * Leaving the playground, whichever door they used.
   *
   * There are three doors — **End session**, **Home** and **Sign out** — and a learner
   * does not think of them as three different things. So they behave as one: whatever
   * they were doing is saved the same way, and the only difference is where they land.
   *
   * The session they never spoke in is the case this exists for. Opening a playground
   * starts a session and the tutor greets them, because a blank screen is a poor
   * welcome — but a learner who came to look at their skill map, or opened the wrong
   * playground, has not had a session and must not be recorded as having had one. So
   * leaving without speaking throws it away. The server is the one that decides: it
   * refuses with a 409 once there is a conversation, and this asks it rather than
   * guessing.
   */
  const leave = useCallback(
    async (after: () => void, { wait }: { wait: boolean }) => {
      if (!session) {
        after();
        return;
      }
      if (spoke && !outcome) {
        // A conversation has to be saved before they go: the update pass is the only
        // thing that turns half an hour of work into a skill map.
        setEnding(true);
        try {
          await save();
        } finally {
          setEnding(false);
        }
        after();
        return;
      }
      if (!spoke) {
        // Their code is theirs even though the session is not — `editor_states` is keyed
        // by learner and domain, and somebody can spend twenty minutes writing a program
        // without once talking to the tutor. Awaited rather than fired alongside the
        // discard, which would otherwise be free to land first and 404 the save.
        await api.saveEditor(session.session_id, code).catch(() => {});
        discarded.current = session.session_id;
        const dropped = api.abandonSession(session.session_id).catch(() => {});
        // Only sign-out waits, and for a reason that has nothing to do with tidiness:
        // it is about to invalidate the cookie this request is authenticated by. Going
        // Home does not wait, because a learner who opened the wrong playground should
        // not be watching a spinner — the server serialises the discard against the
        // greeting on its own.
        if (wait) await dropped;
      }
      after();
    },
    [session, spoke, outcome, save, code],
  );

  /**
   * The Home button on a playground page.
   *
   * With a conversation behind it this *is* End session — same save, same screen
   * afterwards, because a learner who has been talking to the tutor for half an hour
   * should see what moved rather than being dropped back at the picker. Without one
   * there is nothing to save and nothing to show, so it goes straight home.
   */
  const goHome = useCallback(() => {
    if (phase === "finished") {
      toHome();
      return;
    }
    if (spoke && !outcome) {
      void end();
      return;
    }
    void leave(toHome, { wait: false });
  }, [phase, spoke, outcome, end, leave, toHome]);

  /** Signing out is leaving too — the session is saved on the way past, then the door. */
  const signOut = useCallback(() => void leave(onSignOut, { wait: true }), [leave, onSignOut]);

  /**
   * A playground's own name, from its slug.
   *
   * The resume rows used to print `domain_slug` — `strudel` — while the picker three
   * inches above said "Making music with code". Two names for one thing is how a
   * ten-year-old decides they are looking at two different things.
   */
  const titleFor = useMemo(() => {
    const titles = new Map(domains.map((domain) => [domain.slug, domain.title]));
    return (slug: string) => titles.get(slug) ?? slug;
  }, [domains]);

  const runnerNote = useMemo(() => {
    if (!session) return null;
    if (session.runner === "none") {
      return "This domain runs on your own machine — attach your code and say what happened.";
    }
    return null;
  }, [session]);

  if (phase === "finished" && session) {
    return (
      <div className="app">
        <header className="top">
          <span className="brand">QRL Tutor</span>
          <span className="muted">
            {session.display_name} · {session.domain_title} · session {session.session_number}
          </span>
          <WhoAmI me={me} onSignOut={signOut} />
          <button type="button" className="primary go-home" onClick={toHome}>
            Home
          </button>
        </header>
        <div className="panels finished">
          {map ? (
            <SkillMap map={map} litUp={litUp} onClose={toHome} />
          ) : (
            <section className="panel skillmap" aria-label="Skill map">
              <div className="map-detail">
                {mapProblem ? `Could not load the map: ${mapProblem}` : "Loading your map…"}
              </div>
            </section>
          )}
          <section className="panel done">
            <div className="done-card">
              {outcome && <SessionOutcome outcome={outcome} />}
              <button type="button" className="primary" onClick={toHome}>
                Back to Home
              </button>
            </div>
          </section>
        </div>
      </div>
    );
  }

  if (phase !== "live" || !session || !runner) {
    return (
      <>
        {/* A sibling of <main>, never a child of it.
            `.setup.lit` uses `backdrop-filter`, and that makes an element a containing
            block for `position: fixed` descendants — so nesting this inside the card
            clipped the whole background to the card, which is exactly backwards. */}
        <Aurora />
        <main className="setup lit">
        {/* The product name and the staff door on one line. Admin Panel used to sit
            between the heading and the form — a facilitator-only detour planted in the
            middle of the one path a learner is meant to walk, and an orphan in the
            stack besides. Top-right is where people already look for the back of an
            app, and it is out of the way of everything below. */}
        <div className="setup-head">
          <p className="eyebrow">QRL Tutor</p>
          {me.role !== "learner" && (
            <a className="admin-link" href="/facilitator">
              Admin panel
            </a>
          )}
        </div>
        {/* Named, and named on the button that leads back here, because "the screen you
            get after signing in" is otherwise a thing a ten-year-old has no word for. */}
        <h1>Home</h1>
        <p className="lead">
          Pick a playground and start. Your code is waiting where you left it.
        </p>
        {me.role === "learner" && !me.learner_code && (
          <div className="problem" role="alert">
            <strong>Your account is not linked to a learner record yet</strong>
            <p>
              A facilitator needs to finish setting you up. Nothing is wrong with your
              sign-in.
            </p>
          </div>
        )}
        {/* `role="alert"` so a failed start is spoken, not just drawn. Without it the
            only report that starting did not work was a red box someone had to happen
            to be looking at. A consent block is a facilitator's task rather than a
            fault, so it announces politely instead of interrupting. */}
        {problem && (
          <div
            className={consentBlocked ? "problem consent" : "problem"}
            role={consentBlocked ? "status" : "alert"}
          >
            {consentBlocked && <strong>Consent needed before this learner can start</strong>}
            <p>{problem}</p>
            {consentBlocked && (
              <p className="muted">
                The sheet a caregiver reads and signs is <code>docs/consent.md</code>.
              </p>
            )}
          </div>
        )}
        {/* Above the form, not below it.
            An open session is a reconnection — a reload, a dropped connection, Home
            pressed by mistake — and the way back into it used to sit underneath a big
            blue Start button. So the most prominent control on the screen was the one
            that walks away from the half-finished work and opens a second session
            beside it, and the fix for the mistake was the quiet thing further down.
            The likeliest right answer goes first and wears the primary colour; Start
            steps back to secondary for as long as there is one. */}
        {resumable.length > 0 && (
          <section className="resume" aria-labelledby="resume-title">
            <h2 className="resume-title" id="resume-title">
              {resumable.length === 1
                ? "You are still in a session"
                : "You are still in these sessions"}
            </h2>
            <ul className="resume-list">
              {resumable.map((row) => (
                <li key={row.session_id} className="resume-item open">
                  <span className="resume-what">
                    <span className="resume-where">{titleFor(row.domain_slug)}</span>
                    <span className="when">
                      {whenText(row.session_date)} · {row.turn_count} turn
                      {row.turn_count === 1 ? "" : "s"}
                    </span>
                  </span>
                  {/* The label names the playground for anyone who cannot see which row
                      the button is in. Four buttons all called "Go back to it" is a list
                      a screen reader cannot tell apart. */}
                  <button
                    type="button"
                    className="primary"
                    onClick={() => void resume(row.session_id)}
                    disabled={phase === "starting"}
                    aria-label={`Go back to ${titleFor(row.domain_slug)}`}
                  >
                    Go back to it
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}
        <form
          className="start"
          onSubmit={(event) => {
            event.preventDefault();
            void begin();
          }}
        >
          {me.role === "learner" ? (
            <p className="signed-in-as">
              Signed in as <strong>{me.nickname}</strong>
            </p>
          ) : (
            <label>
              Learner code
              <input
                value={learnerCode}
                onChange={(event) => setLearnerCode(event.target.value)}
                spellCheck={false}
                autoComplete="off"
                autoCapitalize="characters"
                placeholder="L-0021"
              />
            </label>
          )}
          {/* Every playground is on the page at once. A dropdown showed one and hid the
              rest, and testers took the one they could see for the whole list. Past
              five or so this stops fitting and will want a rethink. */}
          <fieldset className="playgrounds">
            <legend>{resumable.length > 0 ? "Or start something new" : "Playground"}</legend>
            <div className="playground-list">
              {domains.map((domain) => (
                <label
                  key={domain.slug}
                  className={domain.slug === domainSlug ? "playground picked" : "playground"}
                >
                  <input
                    type="radio"
                    name="playground"
                    value={domain.slug}
                    checked={domain.slug === domainSlug}
                    onChange={() => setDomainSlug(domain.slug)}
                  />
                  <span className="playground-what">
                    <span className="playground-title">{domain.title}</span>
                    {/* How much there is to it. A bare list of three names says nothing
                        about what picking one gets you, and the count is the one honest
                        number we already have. */}
                    <span className="playground-size">{domain.concept_count} skills</span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>
          <button
            type="submit"
            className={resumable.length > 0 ? "start-button" : "primary start-button"}
            disabled={phase === "starting"}
          >
            {phase === "starting" ? "Starting…" : "Start session"}
          </button>
        </form>
        <div className="setup-foot">
          <WhoAmI me={me} onSignOut={signOut} />
        </div>
      </main>
      </>
    );
  }

  return (
    <div className="app">
      <header className="top">
        <span className="brand">QRL Tutor</span>
        <span className="muted">
          {session.display_name} · {session.domain_title} · session {session.session_number}
        </span>
        {runnerNote && <span className="note">{runnerNote}</span>}
        <WhoAmI me={me} onSignOut={signOut} />
        {/* Home is the way out of a playground, and for a learner who has been talking
            to the tutor it *is* End session — same save, same screen afterwards. Two
            buttons that quietly did different things with a half-hour of work would be
            the worst possible arrangement. */}
        <button
          type="button"
          className="primary go-home"
          onClick={goHome}
          disabled={ending}
        >
          {ending ? "Saving…" : "Home"}
        </button>
        <button
          type="button"
          className="ghost map-toggle"
          onClick={() => {
            if (mapOpen) {
              setMapOpen(false);
            } else {
              void openMap(session.learner_code, session.domain_slug);
            }
          }}
        >
          {mapOpen ? "Back to work" : "Skill map"}
        </button>
      </header>
      <div className={mapOpen ? "panels mapping" : "panels"}>
        {mapOpen &&
          (map ? (
            <SkillMap map={map} litUp={litUp} onClose={() => setMapOpen(false)} />
          ) : (
            <section className="panel skillmap" aria-label="Skill map">
              <div className="map-detail">
                {mapProblem ? `Could not load the map: ${mapProblem}` : "Loading your map…"}
              </div>
            </section>
          ))}
        {!mapOpen && (
        <EditorPanel
          code={code}
          onCodeChange={setCode}
          language={runner.language}
          runLabel={runner.runLabel}
          canStop={runner.canStop}
          playing={playing}
          running={running}
          loadingMessage={loadingMessage}
          lastResult={lastResult}
          buffered={buffered}
          stdinText={stdinText}
          onStdinChange={setStdinText}
          needsStdin={!!needsStdin}
          onRun={() => void handleRun()}
          onStop={handleStop}
          needsSurface={!!runner.needsSurface}
          surfaceRef={attachSurface}
          liveLabel={runner.liveLabel ?? "playing"}
          sounds={sounds}
          soundsLoading={soundsLoading}
          soundsError={soundsError}
          onPickSound={pickSound}
          onCursorChange={setCursor}
          asking={asking}
          answerText={answerText}
          onAnswerChange={setAnswerText}
          onAnswerSubmit={sendAnswer}
        />
        )}
        {!mapOpen && (
        <TutorPanel
          displayName={session.display_name}
          messages={messages}
          streaming={streaming}
          draft={draft}
          onDraftChange={setDraft}
          onSend={() => void send()}
          busy={busy}
          bufferedCount={buffered.length}
          onEnd={() => void end()}
          ending={ending}
          outcome={outcome}
          language={runner.language}
        />
        )}
      </div>
    </div>
  );
}
